<template>
  <div ref="modal" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header border-0 pb-0">
          <h5 class="modal-title mb-0">
            <AppIcon name="bi-bell" class="bi bi-bell me-2" />{{ $t('add_reminder') }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label" for="reminder-when">{{ $t('when') }} <span class="text-danger">*</span></label>
            <input
              id="reminder-when"
              v-model="form.remind_at"
              :min="minimumDate"
              type="datetime-local"
              class="form-control"
            >
          </div>
          <div class="mb-3">
            <label class="form-label" for="reminder-note">{{ $t('reminder_note') }} <span class="text-danger">*</span></label>
            <textarea
              id="reminder-note"
              v-model="form.note"
              class="form-control"
              rows="4"
              maxlength="5000"
              :placeholder="$t('reminder_note_placeholder')"
            ></textarea>
          </div>

          <div v-if="loading" class="text-center py-3">
            <span class="spinner-border spinner-border-sm"></span>
          </div>
          <div v-else-if="reminders.length" class="scheduled-reminders border-top pt-3 mt-4">
            <h6 class="small text-muted text-uppercase mb-3">{{ $t('scheduled_reminders') }}</h6>
            <div v-for="reminder in reminders" :key="reminder.id" class="d-flex gap-2 align-items-start border-bottom pb-2 mb-2">
              <AppIcon name="bi-clock" class="bi bi-clock text-primary mt-1" />
              <div class="flex-grow-1 reminder-copy">
                <div class="small fw-bold">{{ formatDateTime(reminder.remind_at) }}</div>
                <div class="small text-muted" style="white-space: pre-wrap; overflow-wrap: anywhere;">{{ reminder.note }}</div>
              </div>
              <button class="btn btn-link btn-sm text-danger p-0" :title="$t('delete')" @click="remove(reminder.id)">
                <AppIcon name="bi-trash" class="bi bi-trash" />
              </button>
            </div>
          </div>
        </div>

        <div class="modal-footer border-0 pt-0">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">{{ $t('close') }}</button>
          <button type="button" class="btn btn-success" :disabled="!canSave || saving" @click="save">
            <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
            <AppIcon name="bi-bell-fill" v-else class="bi bi-bell-fill me-1" />{{ $t('save_reminder') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import helper from '../helper'

export default {
  name: 'ReminderModal',
  props: {
    entityType: { type: String, required: true },
    entityId: { type: String, required: true },
  },
  data() {
    return {
      modal: null,
      form: { remind_at: '', note: '' },
      reminders: [],
      loading: false,
      saving: false,
    }
  },
  computed: {
    minimumDate() {
      return moment().format('YYYY-MM-DDTHH:mm')
    },
    canSave() {
      return this.form.remind_at && this.form.note.trim()
    },
  },
  mounted() {
    // eslint-disable-next-line no-undef
    this.modal = new bootstrap.Modal(this.$refs.modal, { backdrop: 'static' })
  },
  beforeDestroy() {
    if (this.modal) this.modal.dispose()
  },
  methods: {
    defaultReminderTime() {
      return moment().add(1, 'day').hour(9).minute(0).second(0).format('YYYY-MM-DDTHH:mm')
    },
    formatDateTime(value) {
      return moment(value).format('LLL')
    },
    open() {
      this.form = { remind_at: this.defaultReminderTime(), note: '' }
      this.modal.show()
      this.load().catch(() => {})
    },
    async load() {
      if (!this.entityId) return
      try {
        this.loading = true
        const query = new URLSearchParams({
          entity_type: this.entityType,
          entity_id: this.entityId,
          status: 'scheduled',
        })
        const response = await helper.customGet(`${process.env.VUE_APP_API}/reminders?${query.toString()}`)
        this.reminders = response.data.data || []
      } finally {
        this.loading = false
      }
    },
    notifyChanged() {
      window.dispatchEvent(new CustomEvent('reminders:changed'))
    },
    async save() {
      if (!this.canSave) return
      try {
        this.saving = true
        await helper.customPost(`${process.env.VUE_APP_API}/reminders`, {
          entity_type: this.entityType,
          entity_id: this.entityId,
          remind_at: new Date(this.form.remind_at).toISOString(),
          note: this.form.note.trim(),
        })
        this.form = { remind_at: this.defaultReminderTime(), note: '' }
        await this.load()
        this.notifyChanged()
        if (this.$toast) this.$toast.success(this.$t('reminder_created'))
      } finally {
        this.saving = false
      }
    },
    async remove(id) {
      await helper.customDelete(`${process.env.VUE_APP_API}/reminders/${id}`)
      this.reminders = this.reminders.filter(reminder => reminder.id !== id)
      this.notifyChanged()
    },
  },
}
</script>

<style scoped>
.scheduled-reminders {
  max-height: 220px;
  overflow-y: auto;
}

.reminder-copy {
  min-width: 0;
}
</style>
